import { getAge } from '@/utils/getAge';
import { getTemplateById, type AnamnesisTemplate, type AnamnesisTemplateId } from './templates';
import type { Patient } from '@/types/patient';

/** Adolescence starts at 12 (WHO / ECA); legal adulthood in Brazil at 18. */
const ADOLESCENT_MIN_AGE = 12;
const ADULT_MIN_AGE = 18;

export type ResolvedTemplate =
    | { kind: 'matched'; template: AnamnesisTemplate; templateId: AnamnesisTemplateId; age: number }
    | { kind: 'unknown-age' };

/**
 * The patient's age in years, or `null` when nothing usable is registered.
 * `getAge` collapses "invalid date" and "baby" into 0, so the birth date is
 * validated here first — a 6-month-old must still resolve to the infantil
 * template rather than fall back to the manual gallery.
 */
function getPatientAge(patient: Patient): number | null {
    const storedAge = Number(patient.age);
    if (Number.isFinite(storedAge) && storedAge > 0) return storedAge;

    if (!patient.date_of_birth) return null;

    const birthDate = new Date(patient.date_of_birth);
    if (Number.isNaN(birthDate.getTime()) || birthDate > new Date()) return null;

    return getAge(patient.date_of_birth);
}

/** "Ana Souza · 14 anos", or just the name when no age is registered. */
export function describePatient(patient?: Patient): string | undefined {
    if (!patient) return undefined;

    const age = getPatientAge(patient);
    if (age === null) return patient.full_name;

    return `${patient.full_name} · ${age} ${age === 1 ? 'ano' : 'anos'}`;
}

/**
 * The age range a template covers, e.g. "12–17 anos" — shown on the picker cards
 * so a manual choice has the same information the suggestion uses. Derived from
 * the brackets above rather than written out, so the labels cannot drift from the
 * matching rule.
 */
export function getTemplateAgeLabel(templateId: AnamnesisTemplateId): string | undefined {
    switch (templateId) {
        case 'infantil':
            return `0–${ADOLESCENT_MIN_AGE - 1} anos`;
        case 'adolescente':
            return `${ADOLESCENT_MIN_AGE}–${ADULT_MIN_AGE - 1} anos`;
        case 'adulto':
            return `${ADULT_MIN_AGE}+ anos`;
        default:
            return undefined;
    }
}

/**
 * Picks the anamnesis template that fits the patient's age, so the psychologist
 * doesn't have to classify a patient the app already knows. When no usable birth
 * date is registered we say so instead of guessing — the caller falls back to the
 * manual template gallery.
 */
export function resolveTemplateForPatient(patient?: Patient): ResolvedTemplate {
    if (!patient) return { kind: 'unknown-age' };

    const age = getPatientAge(patient);
    if (age === null) return { kind: 'unknown-age' };

    const templateId: AnamnesisTemplateId =
        age < ADOLESCENT_MIN_AGE ? 'infantil' : age < ADULT_MIN_AGE ? 'adolescente' : 'adulto';

    return { kind: 'matched', template: getTemplateById(templateId), templateId, age };
}
